import { withContactsAccess } from "@crm-src/components/page-permission-guard"
import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import { saveBlob } from "../../../lib/download"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { PageHeader } from "@/components/ui/page-header"
import { PageLoader } from "@/components/ui/page-loader"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { EditGroupDialog } from "@crm-src/components/contact-groups/edit-group-dialog"
import { AddMembersDialog } from "@crm-src/components/contact-groups/add-members-dialog"
import { CreateGroupDialog } from "@crm-src/components/contact-groups/create-group-dialog"
import { ViewMembersDialog } from "@crm-src/components/contact-groups/view-members-dialog"
import { ImportContactsDialog } from "@crm-src/components/contact-groups/import-contacts-dialog"
import {
  Users,
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  UserPlus,
  Upload,
  Download,
} from "lucide-react"
import { Pagination, paginationPropsFromMeta } from "@/components/ui/pagination"
import { ContactListsAPI, type ContactList, type ContactListFilters, type ContactListFormData, type ContactListMember, type MemberToAdd } from "@crm-src/lib/contact-lists-api"
import { toggleId } from "@crm-src/lib/selection"
import { memberKey, buildExistingMemberKeys, memberCount, canManageGroup } from "./membership"
import { ContactsAPI, type Contact } from "@crm-src/lib/contacts-api"
import { customersApi } from "@crm-src/lib/customers-api"
import { leadsApi } from "@crm-src/lib/leads-api"
import type { Customer } from "@crm-src/types/customer"
import type { Lead } from "@crm-src/types/lead"
import { useUsers } from "@crm-src/hooks/useUsers"
import { useTableQuery } from "@crm-src/components/table/useTableQuery"
import { useDebouncedSearch } from "@crm-src/components/table/useDebouncedSearch"
import { useLatestRequest } from "@crm-src/components/table/useLatestRequest"
import { buildTableParams } from "@crm-src/components/table/table-query"
import { SortableHeader } from "@crm-src/components/table/SortableHeader"
import { TableFilterBar } from "@crm-src/components/table/TableFilterBar"
import { TableEmptyState } from "@crm-src/components/table/TableEmptyState"
import { CONTACT_GROUPS_TABLE } from "@crm-src/components/table/configs"
import type { TableColumn } from "@crm-src/components/table/configs"
import { buildContactGroupFacetDefs } from "./contact-groups-page-query"
import type { ContactGroupFacetKey } from "./contact-groups-page-query"

const PER_PAGE = 25

/**
 * The **Groups** tab of CRM ▸ Contacts & Groups. `tabs` is the hub's tab
 * strip (`ContactsHubPage`), rendered directly under the shared page title so
 * the two tabs read as one page; the page still owns its own header actions.
 */
function ContactGroupsPage({ tabs }: { tabs?: ReactNode } = {}) {
  const { toast } = useToast()
  // WHICH groups are listed is the server's data scope (crm-api
  // `visible_contact_lists`): everyone with "see all records" gets every group,
  // everyone else its own. There is deliberately no "All Groups" switch — it
  // keyed on the CRM user row's roles, which a dashboard actor's row never has,
  // so a super admin never saw it (2026-10-01).

  // State management
  const [groups, setGroups] = useState<ContactList[]>([])
  const [loading, setLoading] = useState(true)

  // Sort / facet / page state. The URL is the SINGLE source of truth — there is
  // deliberately no `useState` copy of any of it, so a refresh, the back button
  // and a pasted link all reproduce the same view. See components/table/.
  //
  // Annotated, not inferred: TS 5.5 reads a `.filter(...)` callback as a type
  // predicate, so an inferred `facetKeys` would narrow to a different union
  // than the facet defs are built from.
  const facetKeys = useMemo<readonly ContactGroupFacetKey[]>(
    () => CONTACT_GROUPS_TABLE.facetKeys, [],
  )
  const table = useTableQuery({
    defaultSort: CONTACT_GROUPS_TABLE.defaultSort,
    defaultDir: CONTACT_GROUPS_TABLE.defaultDir,
    facetKeys,
  })

  // Only the TEXT box debounces (500ms) — a header or facet click fires at
  // once. The hook also owns the "a new term means page 1 again" reset, which
  // is the part that loops if a page writes it inline. Search text is
  // deliberately NOT in the URL; sort, facets and page are.
  const search = useDebouncedSearch(table.setPage)

  const { users } = useUsers()
  const facetDefs = useMemo(() => buildContactGroupFacetDefs(users), [users])
  // `as const` config entries carry no `align`, so the array needs the explicit
  // element type rather than the literal one TypeScript infers.
  const columns = useMemo<readonly TableColumn[]>(() => CONTACT_GROUPS_TABLE.columns, [])

  // Pagination state (what the LAST response reported — the requested page
  // lives in the URL via `table.page`).
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: PER_PAGE
  })

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [isAddMembersDialogOpen, setIsAddMembersDialogOpen] = useState(false)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<ContactList | null>(null)
  // Per-row actions menu anchor (keyed by group id) — replaces the shadcn
  // DropdownMenu during the MUI 5 migration.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: number } | null>(null)

  // Form data
  const [formData, setFormData] = useState<ContactListFormData>({
    name: "",
    description: "",
  })

  // Members state for view dialog
  const [members, setMembers] = useState<ContactListMember[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [memberSearch, setMemberSearch] = useState("")
  const [memberSearchTerm, setMemberSearchTerm] = useState("")
  const [memberPagination, setMemberPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 25
  })

  // Add members dialog state - browse mode (tabs) with load-more pagination
  const [browseContacts, setBrowseContacts] = useState<Contact[]>([])
  const [browseCustomers, setBrowseCustomers] = useState<Customer[]>([])
  const [browseProspects, setBrowseProspects] = useState<Lead[]>([])
  const [browseTotals, setBrowseTotals] = useState({ customers: 0, prospects: 0, contacts: 0 })
  const [browseLoading, setBrowseLoading] = useState(false)
  const [browseLoadingMore, setBrowseLoadingMore] = useState<string | null>(null)
  const [browsePages, setBrowsePages] = useState({ customers: 1, prospects: 1, contacts: 1 })

  // Add members dialog state - search mode (unified)
  interface SearchResult {
    key: string       // e.g. "customer_123", "lead_45", "contact_789"
    type: 'customer' | 'lead' | 'contact'
    id: number | string
    name: string
    email: string | null
    company: string | null
  }
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [selectedMembersToAdd, setSelectedMembersToAdd] = useState<Set<string>>(new Set())
  const [addMembersSearch, setAddMembersSearch] = useState("")
  const [addMembersSearchTerm, setAddMembersSearchTerm] = useState("")
  const [addMembersSearchLoading, setAddMembersSearchLoading] = useState(false)
  const [addMembersHasSearched, setAddMembersHasSearched] = useState(false)
  const searchAbortRef = useRef<AbortController | null>(null)

  // The exact params the list request sends. Derived, never stored: sort/dir/
  // facets/page come from `table` (i.e. the URL) and the search term from the
  // debounce above.
  //
  // `buildTableParams` is generic over the page's facet-key union, so the params
  // it returns type-check against `ContactListFilters` field by field: a facet
  // key the API does not implement is a compile error here, not a param the
  // server silently drops. No cast.
  const listParams = useMemo<ContactListFilters>(() => ({
    ...buildTableParams(
      { sort: table.sort, dir: table.dir },
      table.facets,
      table.page,
      PER_PAGE,
      search.search,
    ),
    active: true,
  }), [table.sort, table.dir, table.facets, table.page, search.search])

  // Drops a slow earlier response that would otherwise land after a faster
  // later one — easy to hit now that a header or facet click fires immediately.
  // See useLatestRequest.
  const beginRequest = useLatestRequest()

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    const isCurrent = beginRequest()
    setLoading(true)
    try {
      const response = await ContactListsAPI.getAll(listParams)
      if (!isCurrent()) return
      setGroups(response.data)
      setPagination(response.meta?.pagination || {
        current_page: 1,
        total_pages: 1,
        total_count: 0,
        per_page: PER_PAGE
      })
    } catch (error) {
      if (!isCurrent()) return
      console.error("Failed to fetch contact groups:", error)
      toast({
        title: "Error",
        description: "Failed to load contact groups",
        variant: "destructive",
      })
      setGroups([])
    } finally {
      // Guarded: a superseded request must not clear the spinner belonging to
      // the request that superseded it.
      if (isCurrent()) setLoading(false)
    }
  }, [listParams, beginRequest, toast])

  // Load groups on mount and when the query changes
  useEffect(() => {
    fetchGroups()
  }, [fetchGroups])

  // Debounce member search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setMemberSearchTerm(memberSearch)
      setMemberPagination(prev => ({ ...prev, current_page: 1 }))
    }, 400)
    return () => clearTimeout(timer)
  }, [memberSearch])

  // Refetch members when search or page changes
  useEffect(() => {
    if (isViewDialogOpen && selectedGroup) {
      fetchMembers(selectedGroup.id, memberSearchTerm, memberPagination.current_page)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberSearchTerm, memberPagination.current_page])

  // Handle pagination. The requested page lives in the URL, so this writes
  // there rather than into the response-shaped `pagination` state above.
  const handlePageChange = (page: number) => {
    table.setPage(page)
  }

  const handleMemberPageChange = (page: number) => {
    setMemberPagination(prev => ({ ...prev, current_page: page }))
  }

  // Fetch members for a group
  const fetchMembers = async (groupId: number, search?: string, page?: number) => {
    setMembersLoading(true)
    try {
      const response = await ContactListsAPI.getMembers(groupId, {
        search: search || undefined,
        page: page || 1,
        per_page: 25,
      })
      setMembers(response.data)
      setMemberPagination(response.meta?.pagination || {
        current_page: 1,
        total_pages: 1,
        total_count: 0,
        per_page: 25
      })
    } catch (error) {
      console.error("Failed to fetch members:", error)
      toast({
        title: "Error",
        description: "Failed to load group members",
        variant: "destructive",
      })
    } finally {
      setMembersLoading(false)
    }
  }

  // Load initial data for browse tabs
  const loadBrowseData = async () => {
    setBrowseLoading(true)
    try {
      // Sort params, 2026-08-28: every call sends the canonical `sort`/`dir`
      // the API actually reads, on `company` — `name` was never a customers
      // sort key, so the old `sort_by: 'name'` was inert. The LEAD calls used
      // to send no sort at all, because `leads_controller` read only
      // `params[:direction]` and ordered by a `customer_contacts` column under
      // the ?search= path's `SELECT DISTINCT customers.*` — Postgres 42P10, a
      // 500 on this picker's search. That is fixed in crm-api (the `Sortable`
      // concern + `leads_sorting_test.rb`), so prospects are now ordered the
      // same way customers are. Keep the "load more" and search calls below on
      // the SAME sort as this initial load, or page 2 arrives from a different
      // ordering than page 1.
      const [customersResponse, prospectsResponse, contactsResponse] = await Promise.all([
        customersApi.getAll({ per_page: 50, sort: 'company', dir: 'asc' }),
        leadsApi.getAll({ per_page: 50, sort: 'company', dir: 'asc' }),
        ContactsAPI.getContacts({ per_page: 50, sort: 'name', direction: 'asc' })
      ])
      setBrowseCustomers(customersResponse.customers || [])
      setBrowseProspects(prospectsResponse.leads || [])
      setBrowseContacts(contactsResponse.data || [])
      setBrowseTotals({
        customers: customersResponse.pagination?.total_count ?? (customersResponse.customers || []).length,
        prospects: prospectsResponse.pagination?.total_count ?? (prospectsResponse.leads || []).length,
        contacts: contactsResponse.meta?.pagination?.total_count ?? contactsResponse.total_count ?? (contactsResponse.data || []).length,
      })
    } catch (error) {
      console.error('Failed to load contacts:', error)
      toast({
        title: "Error",
        description: "Failed to load contacts",
        variant: "destructive",
      })
    } finally {
      setBrowseLoading(false)
    }
  }

  // Load more items for a specific browse tab
  const loadMoreBrowse = async (type: 'customers' | 'prospects' | 'contacts') => {
    const nextPage = browsePages[type] + 1
    setBrowseLoadingMore(type)
    try {
      if (type === 'customers') {
        const res = await customersApi.getAll({ per_page: 50, page: nextPage, sort: 'company', dir: 'asc' })
        setBrowseCustomers(prev => [...prev, ...(res.customers || [])])
      } else if (type === 'prospects') {
        const res = await leadsApi.getAll({ per_page: 50, page: nextPage, sort: 'company', dir: 'asc' })
        setBrowseProspects(prev => [...prev, ...(res.leads || [])])
      } else {
        const res = await ContactsAPI.getContacts({ per_page: 50, page: nextPage, sort: 'name', direction: 'asc' })
        setBrowseContacts(prev => [...prev, ...(res.data || [])])
      }
      setBrowsePages(prev => ({ ...prev, [type]: nextPage }))
    } catch (error) {
      console.error(`Failed to load more ${type}:`, error)
    } finally {
      setBrowseLoadingMore(null)
    }
  }

  // Server-side search across all entity types (unified)
  const searchAvailableMembers = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSearchResults([])
      setAddMembersHasSearched(false)
      return
    }

    // Abort previous in-flight request
    if (searchAbortRef.current) {
      searchAbortRef.current.abort()
    }
    searchAbortRef.current = new AbortController()

    setAddMembersSearchLoading(true)
    setAddMembersHasSearched(true)
    try {
      const [customersResponse, prospectsResponse, contactsResponse] = await Promise.all([
        customersApi.getAll({ search: term, per_page: 50, sort: 'company', dir: 'asc' }),
        leadsApi.getAll({ search: term, per_page: 50, sort: 'company', dir: 'asc' }),
        ContactsAPI.getContacts({ search: term, per_page: 50, sort: 'name', direction: 'asc' })
      ])

      const results: SearchResult[] = []

      for (const c of (customersResponse.customers || [])) {
        results.push({
          key: `customer_${c.id}`,
          type: 'customer',
          id: c.id,
          name: c.full_name || c.company_name || '',
          email: c.email || null,
          company: c.company_name || null,
        })
      }

      for (const p of (prospectsResponse.leads || [])) {
        results.push({
          key: `lead_${p.id}`,
          type: 'lead',
          id: p.id,
          name: p.full_name || p.company_name || '',
          email: p.email || null,
          company: p.company_name || null,
        })
      }

      for (const c of (contactsResponse.data || [])) {
        results.push({
          key: `contact_${c.id}`,
          type: 'contact',
          id: c.id,
          name: c.full_name || '',
          email: c.email || null,
          company: c.company || null,
        })
      }

      setSearchResults(results)
    } catch (error: any) {
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return
      console.error('Failed to search contacts:', error)
      toast({
        title: "Error",
        description: "Failed to search contacts",
        variant: "destructive",
      })
    } finally {
      setAddMembersSearchLoading(false)
    }
  }, [toast])

  // Debounce add-members search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setAddMembersSearchTerm(addMembersSearch)
    }, 400)
    return () => clearTimeout(timer)
  }, [addMembersSearch])

  // Trigger server-side search when debounced term changes
  useEffect(() => {
    if (isAddMembersDialogOpen && addMembersSearchTerm.trim()) {
      searchAvailableMembers(addMembersSearchTerm)
    } else {
      setSearchResults([])
      setAddMembersHasSearched(false)
    }
  }, [addMembersSearchTerm, isAddMembersDialogOpen, searchAvailableMembers])

  // Handle create group
  const handleCreateGroup = async () => {
    if (!formData.name || formData.name.trim() === "") {
      toast({
        title: "Validation Error",
        description: "Name is required",
        variant: "destructive",
      })
      return
    }

    try {
      await ContactListsAPI.create(formData)
      toast({
        title: "Success",
        description: "Contact group created successfully",
      })
      setIsCreateDialogOpen(false)
      resetForm()
      fetchGroups()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create contact group",
        variant: "destructive",
      })
    }
  }

  // Handle update group
  const handleUpdateGroup = async () => {
    if (!selectedGroup) return

    if (!formData.name || formData.name.trim() === "") {
      toast({
        title: "Validation Error",
        description: "Name is required",
        variant: "destructive",
      })
      return
    }

    try {
      await ContactListsAPI.update(selectedGroup.id, formData)
      toast({
        title: "Success",
        description: "Contact group updated successfully",
      })
      setIsEditDialogOpen(false)
      resetForm()
      fetchGroups()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update contact group",
        variant: "destructive",
      })
    }
  }

  // Handle delete group
  const handleDeleteGroup = async () => {
    if (!selectedGroup) return

    try {
      await ContactListsAPI.delete(selectedGroup.id)
      toast({
        title: "Success",
        description: "Contact group deleted successfully",
      })
      setSelectedGroup(null)
      fetchGroups()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete contact group",
        variant: "destructive",
      })
      throw error
    }
  }

  // Handle add members
  const handleAddMembers = async () => {
    if (!selectedGroup || selectedMembersToAdd.size === 0) return

    const membersToAdd: MemberToAdd[] = Array.from(selectedMembersToAdd).map(key => {
      const [type, id] = key.split('_')
      return { type: type as 'contact' | 'customer' | 'lead', id: parseInt(id) }
    })

    try {
      const response = await ContactListsAPI.addMembers(selectedGroup.id, membersToAdd)
      toast({
        title: "Success",
        description: response.data.message,
      })
      setIsAddMembersDialogOpen(false)
      setSelectedMembersToAdd(new Set())
      setAddMembersSearch("")
      // Refresh the group data
      fetchGroups()
      if (isViewDialogOpen) {
        fetchMembers(selectedGroup.id)
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add members",
        variant: "destructive",
      })
    }
  }

  // Handle remove member
  const handleRemoveMember = async (membershipId: number) => {
    if (!selectedGroup) return

    try {
      await ContactListsAPI.removeMembers(selectedGroup.id, [membershipId])
      toast({
        title: "Success",
        description: "Member removed successfully",
      })
      fetchMembers(selectedGroup.id, memberSearchTerm, memberPagination.current_page)
      fetchGroups()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove member",
        variant: "destructive",
      })
    }
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
    })
    setSelectedGroup(null)
  }

  // Open edit dialog
  const openEditDialog = (group: ContactList) => {
    setSelectedGroup(group)
    setFormData({
      name: group.name,
      description: group.description || "",
    })
    setIsEditDialogOpen(true)
  }

  // Open delete dialog
  const openDeleteDialog = (group: ContactList) => {
    setSelectedGroup(group)
    setIsDeleteDialogOpen(true)
  }

  // Open view dialog
  const openViewDialog = async (group: ContactList) => {
    setSelectedGroup(group)
    setMemberSearch("")
    setMemberSearchTerm("")
    setMemberPagination(prev => ({ ...prev, current_page: 1 }))
    setIsViewDialogOpen(true)
    await fetchMembers(group.id, "", 1)
  }

  // Open add members dialog
  const openAddMembersDialog = async (group: ContactList) => {
    setSelectedGroup(group)
    setSelectedMembersToAdd(new Set())
    setAddMembersSearch("")
    setAddMembersSearchTerm("")
    setSearchResults([])
    setAddMembersHasSearched(false)
    setBrowsePages({ customers: 1, prospects: 1, contacts: 1 })
    setIsAddMembersDialogOpen(true)
    await loadBrowseData()
  }

  // Open import dialog
  const openImportDialog = (group: ContactList) => {
    setSelectedGroup(group)
    setIsImportDialogOpen(true)
  }

  // Handle import complete
  const handleImportComplete = () => {
    fetchGroups()
    if (selectedGroup && isViewDialogOpen) {
      fetchMembers(selectedGroup.id)
    }
  }

  // Handle export
  const handleExport = async (group: ContactList) => {
    try {
      toast({
        title: "Exporting...",
        description: `Preparing export of "${group.name}"`,
      })

      const blob = await ContactListsAPI.export(group.id)

      const safeName = group.name.replace(/[^a-zA-Z0-9\-_]/g, '_').substring(0, 30)
      // Native OS Save-As dialog in the desktop app; browser download on the web.
      await saveBlob(`${safeName}-members-${new Date().toISOString().split('T')[0]}.xlsx`, blob)

      toast({
        title: "Export Complete",
        description: `Successfully exported members from "${group.name}"`,
      })
    } catch (error) {
      console.error("Failed to export:", error)
      toast({
        title: "Error",
        description: "Failed to export group members",
        variant: "destructive",
      })
    }
  }

  // Toggle member selection for adding
  const toggleMemberToAdd = (type: string, id: number | string) => {
    setSelectedMembersToAdd(prev => toggleId(prev, memberKey(type, id)))
  }

  // Existing member keys to exclude from browse/search
  const existingMemberKeys = buildExistingMemberKeys(members)

  // Filter browse data for tabs (no search active)
  const filteredBrowseCustomers = browseCustomers.filter(c => !existingMemberKeys.has(memberKey('customer', c.id)))
  const filteredBrowseProspects = browseProspects.filter(p => !existingMemberKeys.has(memberKey('lead', p.id)))
  const filteredBrowseContacts = browseContacts.filter(c => !existingMemberKeys.has(memberKey('contact', c.id)))

  // Filter search results (unified, when searching)
  const filteredSearchResults = searchResults.filter(r => {
    const memberTypeMap: Record<string, string> = { customer: 'Customer', lead: 'Lead', contact: 'Contact' }
    return !members.some(m => m.member_type === memberTypeMap[r.type] && m.member_id === r.id)
  })

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Contacts & Groups"
        description="Create and manage groups of contacts for email campaigns"
        actions={
          <>
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => {
                resetForm()
                setIsCreateDialogOpen(true)
              }}
            >
              Create Group
            </Button>
          </>
        }
      />

      {tabs}

      <Card>
        <CardHeader
          // The header now hosts a whole filter row, not one field: render it
          // as a <div> so the Boxes/Selects inside are not nested in a <span>.
          titleTypographyProps={{ component: "div" }}
          title={
            // The search box is WRAPPED, not replaced — same field, same 500ms
            // debounce; the Owner facet simply sits beside it.
            <TableFilterBar
              facetDefs={facetDefs}
              values={table.facets}
              onChange={table.setFacet}
              onClearAll={table.clearFacets}
              searchSlot={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ display: "flex", color: "text.secondary" }}><Search size={16} /></Box>
                  <TextField
                    placeholder="Search groups..."
                    value={search.input}
                    onChange={(e) => search.setInput(e.target.value)}
                    size="small"
                    sx={{ width: { xs: "100%", sm: 340 } }}
                  />
                  {search.isSearching && (
                    <Typography component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                      Searching...
                    </Typography>
                  )}
                </Box>
              }
            />
          }
        />
        <CardContent>
          {loading ? (
            <PageLoader />
          ) : groups.length === 0 ? (
            // Names whichever narrowing actually emptied the list: filtering to
            // nothing used to claim there were no groups at all.
            <TableEmptyState
              entityNoun="contact groups"
              hasSearch={search.input.trim().length > 0}
              activeFilterCount={table.activeFilterCount}
              onClearFilters={table.clearFacets}
              emptyTitle="No contact groups found"
              emptyBody="Create your first group to get started."
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {/* The five DATA columns, in `CONTACT_GROUPS_TABLE` order —
                        which is the order the body cells below are written in,
                        so the two must be edited together. The actions cell
                        stays a plain <TableCell>: it maps to no server column,
                        so it cannot sort. */}
                    {columns.map((col) => (
                      <SortableHeader
                        key={col.key}
                        column={col.key}
                        label={col.label}
                        sort={table.sort}
                        dir={table.dir}
                        onSort={table.toggleSort}
                        {...(col.align ? { align: col.align } : {})}
                      />
                    ))}
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((group) => (
                      <TableRow
                        key={group.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => openViewDialog(group)}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ display: "flex", color: "warning.main" }}><Users size={16} /></Box>
                            {group.name}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: "text.secondary", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {group.description || <Typography component="span" sx={{ color: "text.disabled" }}>No description</Typography>}
                        </TableCell>
                        <TableCell align="right">
                          {/* `memberCount`, not `members_count || contacts_count`:
                              `||` treats a genuine ZERO as falsy and falls through
                              to the denormalised (and skippable) contacts_count, so
                              an empty group could read "999 members". See
                              membership.ts. This is the number the server's
                              `members` sort orders on. */}
                          <Chip size="small" color="default" label={`${memberCount(group)} member${memberCount(group) !== 1 ? 's' : ''}`} />
                        </TableCell>
                        <TableCell>
                          {group.owner?.full_name || <Typography component="span" sx={{ color: "text.disabled" }}>-</Typography>}
                        </TableCell>
                        <TableCell sx={{ color: "text.disabled" }}>
                          {new Date(group.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            sx={{ width: 32, height: 32 }}
                            aria-label="Open menu"
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuAnchor({ el: e.currentTarget, id: group.id })
                            }}
                          >
                            <MoreHorizontal size={16} />
                          </IconButton>
                          <Menu
                            anchorEl={menuAnchor?.id === group.id ? menuAnchor.el : null}
                            open={menuAnchor?.id === group.id}
                            onClose={() => setMenuAnchor(null)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            // The Menu portals into <body>, but React events bubble
                            // through the REACT tree — so a click on any item also
                            // reached the TableRow's onClick and opened View Members
                            // on top of whatever the item had just opened. The
                            // IconButton already stops it; the items need it too.
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MenuItem onClick={() => {
                              setMenuAnchor(null)
                              openViewDialog(group)
                            }}>
                              <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
                              View Members
                            </MenuItem>
                            {canManageGroup(group) && (
                            <MenuItem onClick={() => {
                              setMenuAnchor(null)
                              openAddMembersDialog(group)
                            }}>
                              <Box sx={{ mr: 1, display: "flex" }}><UserPlus size={16} /></Box>
                              Add Members
                            </MenuItem>
                            )}
                            {canManageGroup(group) && (
                            <MenuItem onClick={() => {
                              setMenuAnchor(null)
                              openImportDialog(group)
                            }}>
                              <Box sx={{ mr: 1, display: "flex" }}><Upload size={16} /></Box>
                              Import Contacts
                            </MenuItem>
                            )}
                            <MenuItem onClick={() => {
                              setMenuAnchor(null)
                              handleExport(group)
                            }}>
                              <Box sx={{ mr: 1, display: "flex" }}><Download size={16} /></Box>
                              Export Members
                            </MenuItem>
                            {/* MUI Menu wants direct children, not a Fragment. */}
                            {canManageGroup(group) && (
                            <MenuItem onClick={() => {
                              setMenuAnchor(null)
                              openEditDialog(group)
                            }}>
                              <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                              Edit
                            </MenuItem>
                            )}
                            {canManageGroup(group) && <Divider />}
                            {canManageGroup(group) && (
                            <MenuItem
                              onClick={() => {
                                setMenuAnchor(null)
                                openDeleteDialog(group)
                              }}
                              sx={{ color: "error.main" }}
                            >
                              <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                              Delete
                            </MenuItem>
                            )}
                          </Menu>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          <Pagination
            {...paginationPropsFromMeta(pagination)}
            entityNoun="groups"
            hideIfSinglePage
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* Create Group Dialog */}
      <CreateGroupDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreateGroup}
      />

      {/* Edit Group Dialog */}
      <EditGroupDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdateGroup}
      />

      {/* Delete Confirmation Dialog. `contact_lists#destroy` calls
          `ContactList#soft_delete!` (`update_column(:deleted_at, …)`) under a
          `not_deleted` default_scope, and contact_list_memberships are not
          cascaded. See docs/crm.md → "Delete is a soft delete". */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Delete Contact Group"
        description={
          <>
            Are you sure you want to delete &quot;{selectedGroup?.name}&quot;? The group is removed from
            the contact-group lists. Its contacts are not deleted, and the group record is kept, so
            an administrator can restore it.
          </>
        }
        confirmLabel="Delete Group"
        onConfirm={handleDeleteGroup}
      />

      {/* View Members Dialog */}
      <ViewMembersDialog
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        selectedGroup={selectedGroup}
        members={members}
        membersLoading={membersLoading}
        memberSearch={memberSearch}
        onMemberSearchChange={setMemberSearch}
        memberSearchTerm={memberSearchTerm}
        memberPagination={memberPagination}
        onMemberPageChange={handleMemberPageChange}
        onExport={handleExport}
        onOpenImport={openImportDialog}
        onOpenAddMembers={openAddMembersDialog}
        onRemoveMember={handleRemoveMember}
      />

      {/* Add Members Dialog */}
      <AddMembersDialog
        open={isAddMembersDialogOpen}
        onOpenChange={setIsAddMembersDialogOpen}
        selectedGroup={selectedGroup}
        addMembersSearch={addMembersSearch}
        onAddMembersSearchChange={setAddMembersSearch}
        addMembersSearchTerm={addMembersSearchTerm}
        addMembersSearchLoading={addMembersSearchLoading}
        addMembersHasSearched={addMembersHasSearched}
        filteredSearchResults={filteredSearchResults}
        selectedMembersToAdd={selectedMembersToAdd}
        onToggleMember={toggleMemberToAdd}
        browseLoading={browseLoading}
        browseLoadingMore={browseLoadingMore}
        browseTotals={browseTotals}
        browseCustomers={browseCustomers}
        browseProspects={browseProspects}
        browseContacts={browseContacts}
        filteredBrowseCustomers={filteredBrowseCustomers}
        filteredBrowseProspects={filteredBrowseProspects}
        filteredBrowseContacts={filteredBrowseContacts}
        onLoadMore={loadMoreBrowse}
        onAddMembers={handleAddMembers}
      />

      {/* Import Contacts Dialog */}
      <ImportContactsDialog
        isOpen={isImportDialogOpen}
        onClose={() => {
          setIsImportDialogOpen(false)
          setSelectedGroup(null)
        }}
        selectedGroup={selectedGroup}
        onImportComplete={handleImportComplete}
      />
    </Box>
  )
}

export default withContactsAccess(ContactGroupsPage)
